/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import React, {
  useCallback,
  useMemo,
  useState
} from 'react';
import cloneDeep from 'lodash.clonedeep';
import {translate} from '@ion/core/src/locale/locale';
import {getGroup} from '@ion/core/src/services/Connect';
import {
  AccountLink,
  GenericObject,
  Groups
} from '@ion/core/src/types/entities';
import {TertiaryButton} from '@ion/dls/src/components/buttons/TertiaryButton';
import {
  FormItem
} from '@ion/dls/src/components/forms/FormItem';
import {
  FormRadioGroup
} from '@ion/dls/src/components/forms/FormRadioGroup';
import {
  FormTextField
} from '@ion/dls/src/components/forms/FormTextField';
import styles from '@ion/dls/src/editors/editorStyles';
import GroupChooser from '@ion/dls/src/editors/GroupChooser';
import {useExtendStyles} from '@ion/dls/src/hooks/useExtendStyles';
import {dlsAlt, dlsColor} from '@ion/dls/src/theme/theme';
import {DLSTheme} from '@ion/dls/src/types/types';
import {AdminGroupChooser} from '@ion/features/src/entities/group/AdminGroupChooser';
import {
  checkGroupNameExists,
  getAccountLink,
  getActionLastIndex,
  GroupActions,
  GroupActionStatus,
  validateActionExists
} from './BulkEditGroupUtils';

export interface GroupActionContainerProps {
  account?: AccountLink;
  selectedAccount?: AccountLink;
  allGroups?: Groups;
  replaceGroups?: GenericObject[];
  setSelectedAccount?: Function;
  setAccountsLoading?: Function;
  groupAction?: string;
  setGroupAction?: Function;
  actionArray?: GenericObject[];
  setActionArray?: Function;
  isGroupsLoading?: boolean;
  formSubmitted?: boolean;
  noGroups?: boolean;
}

const styleOverrides = (theme: DLSTheme) => ({
  formSectionTitle : {
    ...dlsAlt('typography', 'cta3'),
    textTransform : 'uppercase',
    padding       : '10px 0'
  },
  leftContainer : {
    flex         : 1,
    margin       : '10px 0',
    paddingRight : '30px',
    borderRight  : `1px solid ${dlsColor('grey5')}`
  },
  accountStyle : {
    paddingTop : '15px'
  },
  paddingTop25 : {
    paddingTop : '25px'
  },
  actionButton : {
    paddingTop     : '25px',
    display        : 'flex',
    justifyContent : 'center',
    alignItems     : 'center'
  }
});

/**
 * The GroupActionContainer with set fields and encapsulated logic
 * for handling the form's state, including validation.
 *
 * @param {Function} onCloseDialog: Callback function fired when the dialog requests to be closed
 * @param {Devices} selectedDevices: selected Devices to perform bulk operation
 */
export const GroupActionContainer = ({
  account,
  selectedAccount,
  allGroups,
  replaceGroups,
  setAccountsLoading,
  setSelectedAccount,
  groupAction,
  setGroupAction,
  actionArray,
  setActionArray,
  noGroups = false,
  isGroupsLoading = false,
  formSubmitted = false
}: GroupActionContainerProps) => {
  const dialogStyles = useExtendStyles(styles, styleOverrides);
  const classes      = dialogStyles();

  const [addGroup, setAddGroup]                   = useState(null);
  const [removeGroup, setRemoveGroup]             = useState(null);
  const [fromGroup, setFromGroup]                 = useState(null);
  const [fromAccount, setFromAccount]             = useState(null);
  const [toGroup, setToGroup]                     = useState(null);
  const [addGroupError, setAddGroupError]         = useState<boolean>(false);
  const [removeGroupError, setRemoveGroupError]   = useState<boolean>(false);
  const [fromGroupError, setFromGroupError]       = useState<boolean>(false);
  const [toGroupError, setToGroupError]           = useState<boolean>(false);
  const [accountError, setAccountError]           = useState<boolean>(false);
  const [actionExists, setActionExists]           = useState<boolean>(false);
  const [isNewGroup, setIsNewGroup]               = useState<boolean>(false);
  const [groupName, setGroupName]                 = useState<string>('');
  const [groupNotes, setGroupNotes]               = useState<string>('');
  const [isGroupNameEmpty, setIsGroupNameEmpty]   = useState<boolean>(false);
  const [isGroupNameExists, setIsGroupNameExists] = useState<boolean>(false);

  const groupOptions = useMemo(() => ([{
    label    : translate('admin-groups:edit.bulk.addToGroup'),
    value    : GroupActions.Add,
    disabled : false
  },
  {
    label    : translate('admin-groups:edit.bulk.removeFromGroup'),
    value    : GroupActions.Remove,
    disabled : false
  },
  {
    label    : translate('admin-groups:edit.bulk.replaceGroup'),
    value    : GroupActions.Replace,
    disabled : false
  }]), []);

  const handleRadioGroupChange = useCallback((value, name) => {
    setGroupAction(value);
    resetNewGroupFields();
    setIsNewGroup(false);
    setAddGroupError(false);
    setRemoveGroupError(false);
    setFromGroupError(false);
    setToGroupError(false);
    setActionExists(false);
    setAccountError(false);
  }, []);

  const resetNewGroupFields = useCallback(() => {
    setGroupName('');
    setGroupNotes('');
    setIsGroupNameEmpty(false);
    setIsGroupNameExists(false);
  }, []);

  const onRemoveGroupChange = useCallback((value) => {
    setActionExists(false);
    setRemoveGroup(value);
    setRemoveGroupError(false);
  }, []);

  const onFromGroupChange = useCallback((value) => {
    setActionExists(false);
    setFromGroup(value);
    setFromGroupError(false);
    (async function() {
      const groupData = await getGroup(value?.id);
      setFromAccount(groupData?.account);
    })();
  }, []);

  const onAccountChange = useCallback((value) => {
    setSelectedAccount(value);
    setActionExists(false);
    setAddGroup(null);
    setAddGroupError(false);
    setToGroup(null);
    setToGroupError(false);
  }, [setSelectedAccount]);

  const onGroupChange = useCallback((value) => {
    setActionExists(false);
    setIsNewGroup(!value);
    if (groupAction === GroupActions.Add) {
      setAddGroup(value);
      setAddGroupError(false);
    } else {
      setToGroup(value);
      setToGroupError(false);
    }
    resetNewGroupFields();
  }, [groupAction]);

  function onAction() {
    if (groupAction === GroupActions.Add) {
      addGroupAction();
    } else if (groupAction === GroupActions.Remove) {
      removeGroupAction();
    } else if (groupAction === GroupActions.Replace) {
      replaceGroupAction();
    }
  }

  const addGroupAction = () => {
    const actionList = cloneDeep(actionArray);
    let validation = true;
    setAccountError(!selectedAccount);
    setAddGroupError(isNewGroup ? false : !addGroup);
    if (selectedAccount) {
      if (isNewGroup) {
        validation = false;
        setIsGroupNameEmpty(!groupName);
        const groupExists = checkGroupNameExists(allGroups, groupName);
        setIsGroupNameExists(groupExists);
        if (groupName && !groupExists) {
          validation = true;
        }
      } else if (!addGroup) {
        validation = false;
      }
      if (validation) {
        const grpTitle = isNewGroup ? groupName : addGroup?.name;
        const actionValid = validateActionExists(actionList, grpTitle, selectedAccount?.title);
        setActionExists(actionValid);
        if (!actionValid) {
          const dataFor = isNewGroup ? GroupActions.AddNew : GroupActions.Add;
          const actionText = isNewGroup ? translate('admin-groups:edit.bulk.addNewGroupText', {groupName: grpTitle, accountName: account?.title})
            : translate('admin-groups:edit.bulk.addGroupText', {groupName: grpTitle, accountName: selectedAccount?.title});
          const pushData = {
            actionText : actionText,
            groupId    : !isNewGroup ? addGroup?.id : '',
            groupName  : grpTitle,
            groupNotes : isNewGroup ? groupNotes : addGroup?.notes,
            dataFor    : dataFor,
            status     : GroupActionStatus.Hold,
            account    : getAccountLink(selectedAccount)
          };
          const index = getActionLastIndex(actionList, dataFor);
          if (index === -1) {
            const index = getActionLastIndex(actionList, GroupActions.Remove);
            if (index === -1) {
              actionList.unshift(pushData);
            } else {
              actionList.splice(index+1, 0, pushData);
            }
          } else {
            actionList.splice(index+1, 0, pushData);
          }
          setActionArray(actionList);
          if (isNewGroup) {
            setGroupName('');
            setGroupNotes('');
          }
        }
      }
    }
  };

  const removeGroupAction = () => {
    const actionList = cloneDeep(actionArray);
    setRemoveGroupError(!removeGroup);
    if (removeGroup) {
      const actionValid = validateActionExists(actionList, removeGroup?.name, account?.title);
      setActionExists(actionValid);
      if (!actionValid) {
        const grpTitle = removeGroup?.name;
        const pushData = {
          actionText : translate('admin-groups:edit.bulk.removeGroupText', {groupName: grpTitle}),
          groupId    : removeGroup?.id,
          groupName  : grpTitle,
          groupNotes : '',
          dataFor    : GroupActions.Remove,
          status     : GroupActionStatus.Hold,
          account    : account
        };
        const index = getActionLastIndex(actionList, GroupActions.Remove);
        if (index === -1) {
          actionList.unshift(pushData);
        } else {
          actionList.splice(index+1, 0, pushData);
        }
        setActionArray(actionList);
      }
    }
  };

  const replaceGroupAction = () => {
    const actionList = cloneDeep(actionArray);
    let validation = true;
    setFromGroupError(!fromGroup);
    setToGroupError(isNewGroup ? false : !toGroup);
    if (fromGroup) {
      const actionValid = validateActionExists(actionList, fromGroup?.name, account?.title);
      setActionExists(actionValid);
      validation = !actionValid;
    }
    if (isNewGroup) {
      validation = false;
      setIsGroupNameEmpty(!groupName);
      const groupExists = checkGroupNameExists(allGroups, groupName);
      setIsGroupNameExists(groupExists);
      if (groupName && !groupExists) {
        validation = true;
      }
    } else if (!toGroup) {
      validation = false;
    }
    if (validation) {
      const grpTitle = isNewGroup ? groupName : toGroup?.name;
      const actionValid = validateActionExists(actionList, grpTitle, selectedAccount?.title);
      setActionExists(actionValid);
      if (!actionValid) {
        const dataFor = isNewGroup ? GroupActions.ReplaceNew : GroupActions.Replace;
        const actionText = isNewGroup
          ? translate('admin-groups:edit.bulk.replaceNewGroupText', {
            fromGroupName   : fromGroup?.name,
            fromAccountName : fromAccount?.title,
            newGroupName    : grpTitle,
            accountName     : selectedAccount?.title
          })
          : translate('admin-groups:edit.bulk.replaceGroupText', {
            fromGroupName   : fromGroup?.name,
            fromAccountName : fromAccount?.title,
            groupName       : grpTitle,
            accountName     : selectedAccount?.title
          });
        const pushData = {
          actionText  : actionText,
          groupId     : fromGroup?.id,
          groupName   : fromGroup?.name,
          toGroupId   : !isNewGroup ? toGroup?.id : '',
          toGroupName : grpTitle,
          groupNotes  : isNewGroup ? groupNotes : toGroup?.notes,
          dataFor     : dataFor,
          status      : GroupActionStatus.Hold,
          account     : getAccountLink(selectedAccount)
        };
        actionList.push(pushData);
        setActionArray(actionList);
        if (isNewGroup) {
          setGroupName('');
          setGroupNotes('');
        }
      }
    }
  };

  return (
    <div className={classes.leftContainer}>
      <>
        <h1 className={classes.formSectionTitle}>
          {translate('admin-groups:edit.bulk.groupAction')}
        </h1>
        <FormItem fullWidth>
          <FormRadioGroup options={groupOptions}
            name={'groupOption'}
            groupLabel={translate('admin-groups:edit.bulk.actions')}
            onChange={handleRadioGroupChange}
            defaultValue={groupAction} />
        </FormItem>
        {groupAction === GroupActions.Remove && (
          <div className={classes.paddingTop25}>
            <FormItem fullWidth>
              <AdminGroupChooser fieldLabel={translate('admin-groups:edit.bulk.group')}
                groupOptions={replaceGroups}
                loading={isGroupsLoading}
                onChange={onRemoveGroupChange}
                hasError={removeGroupError || actionExists}
                errorText={removeGroupError
                  ? translate('common:features.groups.error')
                  : translate('admin-groups:edit.bulk.groupExistError')
                }
                defaultValue={removeGroup || ''} />
            </FormItem>
          </div>
        )}
        {groupAction === GroupActions.Replace && (
          <div className={classes.paddingTop25}>
            <FormItem fullWidth>
              <AdminGroupChooser fieldLabel={translate('admin-groups:edit.bulk.fromGroup')}
                groupOptions={replaceGroups}
                loading={isGroupsLoading}
                onChange={onFromGroupChange}
                hasError={fromGroupError || actionExists ||
                (fromGroup && toGroup && fromGroup?.id === toGroup?.id)}
                errorText={(fromGroup && toGroup && (fromGroup?.id === toGroup?.id))
                  ? translate('admin-groups:edit.bulk.groupsNotSame')
                  : (actionExists
                    ? translate('admin-groups:edit.bulk.groupExistError')
                    : translate('common:features.groups.error'))
                }
                defaultValue={fromGroup || ''} />
            </FormItem>
          </div>
        )}
        {groupAction !== GroupActions.Remove && (
          <>
            <div className={classes.accountStyle}>
              <FormItem fullWidth>
                <GroupChooser onLoad={setAccountsLoading}
                  account={account}
                  includeGroupProperties={false}
                  onChange={onAccountChange}
                  hasError={accountError}
                  defaultValue={selectedAccount || account} />
              </FormItem>
            </div>
            <div className={classes.paddingTop25}>
              <FormItem fullWidth>
                <AdminGroupChooser includeNone
                  noneOptionValue={'newGroup'}
                  noneOptionLabel={translate('admin-groups:edit.bulk.newGroup')}
                  fieldLabel={groupAction === GroupActions.Replace ? translate('admin-groups:edit.bulk.toGroup') : ''}
                  groupOptions={allGroups}
                  loading={isGroupsLoading}
                  onChange={onGroupChange}
                  hasError={!isNewGroup && (
                    groupAction === GroupActions.Add
                      ? (addGroupError || actionExists)
                      : (toGroupError || actionExists ||
                        (fromGroup && toGroup && fromGroup?.id === toGroup?.id)
                      )
                  )}
                  errorText={(fromGroup && toGroup && (fromGroup?.id === toGroup?.id))
                    ? translate('admin-groups:edit.bulk.groupsNotSame')
                    : (actionExists
                      ? translate('admin-groups:edit.bulk.groupExistError')
                      : translate('common:features.groups.error'))
                  }
                  defaultValue={(groupAction === GroupActions.Add ? addGroup : toGroup) || ''} />
              </FormItem>
            </div>
          </>
        )}
        {isNewGroup && (
          <>
            <div className={classes.paddingTop25}>
              <FormItem fullWidth>
                <FormTextField required
                  name={'groupName'}
                  fieldLabel={translate('admin-groups:edit.bulk.groupName.label')}
                  placeholder={translate('admin-groups:edit.bulk.groupName.placeholder')}
                  onChange={function(event) {
                    setGroupName(event?.target?.value);
                  }}
                  hasError={isGroupNameEmpty || isGroupNameExists}
                  errorText={!groupName
                    ? translate('common:editors.shared.required')
                    : translate('admin-groups:edit.bulk.groupName.error')}
                  value={groupName || ''}
                  maxLength={64} />
              </FormItem>
            </div>
            <div className={classes.paddingTop25}>
              <FormItem fullWidth>
                <FormTextField name={'notes'}
                  fieldLabel={translate('admin-groups:edit.bulk.notes.label')}
                  placeholder={translate('admin-groups:edit.bulk.notes.placeholder')}
                  onChange={function(event) {
                    setGroupNotes(event?.target?.value);
                  }}
                  hasError={groupNotes?.length > 1024}
                  errorText={translate('admin-groups:edit.bulk.notes.error')}
                  value={groupNotes || ''}
                  multiline
                  rows={1}
                  rowsMax={5}
                  maxLength={1024} />
              </FormItem>
            </div>
          </>
        )}
        <div className={classes.actionButton}>
          <TertiaryButton onClick={onAction} disabled={noGroups || formSubmitted}>
            {groupAction}
          </TertiaryButton>
        </div>
      </>
    </div>
  );
};

export default GroupActionContainer;
